/**
 * Data Sources Hook
 * TanStack Query hook for managing data source connections
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import type { DataSource, CreateDataSourceInput } from '../models/DataSource';

// Query key factory with const assertion for type safety
export const dataSourceKeys = {
  all: ['data-sources'] as const,
  list: (orgId: string) => [...dataSourceKeys.all, 'list', orgId] as const,
  detail: (id: string) => [...dataSourceKeys.all, 'detail', id] as const,
  schema: (id: string) => [...dataSourceKeys.all, 'schema', id] as const,
  googleSheets: (id: string, spreadsheetId: string) =>
    [...dataSourceKeys.all, 'google-sheets', id, spreadsheetId] as const,
  excelDrives: (id: string) => [...dataSourceKeys.all, 'excel-drives', id] as const,
  excelWorkbooks: (id: string, driveId: string) => [...dataSourceKeys.all, 'excel-workbooks', id, driveId] as const,
  excelWorksheets: (id: string, driveId: string, workbookId: string) =>
    [...dataSourceKeys.all, 'excel-worksheets', id, driveId, workbookId] as const,
  records: (id: string, params?: { limit?: number; offset?: number }) =>
    [...dataSourceKeys.all, 'records', id, params] as const,
};

interface DataSourcesResponse {
  dataSources: DataSource[];
}

interface DataSourceResponse {
  dataSource: DataSource;
}

interface SchemaResponse {
  schema: {
    columns: Array<{ name: string; type: string }>;
  };
  suggestions: Array<{
    sourceColumn: string;
    targetField: string;
    confidence: number;
    suggestedTransform?: string;
  }>;
  suggestedKeyColumn: string | null;
}

interface RecordsResponse {
  records: Array<{ id: string; data: Record<string, unknown> }>;
  total: number;
  hasMore: boolean;
}

interface ExcelWorkbooksResponse {
  workbooks: Array<{ id: string; name: string; webUrl?: string }>;
}

interface ExcelDrivesResponse {
  drives: Array<{ id: string; name: string; driveType: string }>;
}

interface ExcelWorksheetsResponse {
  worksheets: Array<{ id: string; name: string; position: number }>;
}

interface GoogleSheetsResponse {
  spreadsheet: { id: string; title: string };
  sheets: Array<{ id: string; name: string; rowCount?: number }>;
}

const HIDDEN_DATA_SOURCE_PROVIDERS = new Set(['csv']);

/**
 * Hook to fetch all data sources for the org
 */
export function useDataSources() {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery<DataSourcesResponse>({
    queryKey: dataSourceKeys.all,
    queryFn: async () => {
      const res = await authenticatedFetch('/api/data-sources');
      if (!res.ok) throw new Error('Failed to load data sources');
      const payload = await res.json() as DataSourcesResponse;
      return {
        ...payload,
        dataSources: payload.dataSources.filter(
          (source) => !HIDDEN_DATA_SOURCE_PROVIDERS.has(source.provider),
        ),
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch data sources filtered by scope
 * Returns org-wide sources everywhere, plus folder-specific sources when in that folder
 * @param folderId - Current folder context (null for org-level views)
 */
export function useScopedDataSources(folderId: string | null) {
  const { data, ...rest } = useDataSources();

  const filteredDataSources = data?.dataSources.filter((ds) => {
    // Org-scoped data sources are available everywhere
    if (ds.scope === 'org' || !ds.scope) {
      return true;
    }
    // Folder-scoped data sources are only available in their folder
    if (ds.scope === 'folder') {
      return ds.folderId === folderId;
    }
    return false;
  });

  return {
    ...rest,
    data: data ? { dataSources: filteredDataSources ?? [] } : undefined,
    allDataSources: data?.dataSources ?? [],
  };
}

/**
 * Hook to fetch a single data source
 */
export function useDataSource(id: string | undefined) {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery<DataSourceResponse>({
    queryKey: dataSourceKeys.detail(id ?? ''),
    queryFn: async () => {
      const res = await authenticatedFetch(`/api/data-sources/${id}`);
      if (!res.ok) throw new Error('Data source not found');
      return res.json();
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch data source schema with mapping suggestions
 */
export function useDataSourceSchema(id: string | undefined) {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery<SchemaResponse>({
    queryKey: dataSourceKeys.schema(id ?? ''),
    queryFn: async () => {
      const res = await authenticatedFetch(`/api/data-sources/${id}/schema`);
      if (!res.ok) throw new Error('Schema not available');
      return res.json();
    },
    enabled: !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useExcelDrives(sourceId: string | undefined, enabled = true) {
  const authenticatedFetch = useAuthenticatedFetch();
  return useQuery<ExcelDrivesResponse>({
    queryKey: dataSourceKeys.excelDrives(sourceId ?? ''),
    queryFn: async () => {
      const res = await authenticatedFetch(`/api/data-sources/${sourceId}/excel/drives`);
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error((error as { error?: string }).error ?? 'Failed to load OneDrive drives');
      }
      return res.json();
    },
    enabled: !!sourceId && enabled,
    staleTime: 2 * 60 * 1000,
  });
}

export function useExcelWorkbooks(sourceId: string | undefined, driveId: string | undefined, enabled = true) {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery<ExcelWorkbooksResponse>({
    queryKey: dataSourceKeys.excelWorkbooks(sourceId ?? '', driveId ?? ''),
    queryFn: async () => {
      const res = await authenticatedFetch(`/api/data-sources/${sourceId}/excel/workbooks?driveId=${encodeURIComponent(driveId ?? '')}`);
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error((error as { error?: string }).error ?? 'Failed to load Excel workbooks');
      }
      return res.json();
    },
    enabled: !!sourceId && !!driveId && enabled,
    staleTime: 2 * 60 * 1000,
  });
}

export function useGoogleSheets(
  sourceId: string | undefined,
  spreadsheetId: string | undefined,
  enabled = true
) {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery<GoogleSheetsResponse>({
    queryKey: dataSourceKeys.googleSheets(sourceId ?? '', spreadsheetId ?? ''),
    queryFn: async () => {
      const encodedSpreadsheetId = encodeURIComponent(spreadsheetId ?? '');
      const res = await authenticatedFetch(
        `/api/data-sources/${sourceId}/google/sheets?spreadsheetId=${encodedSpreadsheetId}`
      );
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error((error as { error?: string }).error ?? 'Failed to load Google sheets');
      }
      return res.json();
    },
    enabled: !!sourceId && !!spreadsheetId && enabled,
    staleTime: 2 * 60 * 1000,
  });
}

export function useExcelWorksheets(
  sourceId: string | undefined,
  driveId: string | undefined,
  workbookId: string | undefined,
  enabled = true
) {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery<ExcelWorksheetsResponse>({
    queryKey: dataSourceKeys.excelWorksheets(sourceId ?? '', driveId ?? '', workbookId ?? ''),
    queryFn: async () => {
      const encodedWorkbookId = encodeURIComponent(workbookId ?? '');
      const res = await authenticatedFetch(
        `/api/data-sources/${sourceId}/excel/workbooks/${encodedWorkbookId}/worksheets?driveId=${encodeURIComponent(driveId ?? '')}`
      );
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error((error as { error?: string }).error ?? 'Failed to load worksheets');
      }
      return res.json();
    },
    enabled: !!sourceId && !!driveId && !!workbookId && enabled,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook to fetch records from a data source
 */
export function useExternalRecords(
  sourceId: string | undefined,
  options?: { limit?: number; offset?: number }
) {
  const authenticatedFetch = useAuthenticatedFetch();
  const limit = options?.limit ?? 100;
  const offset = options?.offset ?? 0;

  return useQuery<RecordsResponse>({
    queryKey: dataSourceKeys.records(sourceId ?? '', { limit, offset }),
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });
      const res = await authenticatedFetch(
        `/api/data-sources/${sourceId}/records?${params}`,
        {
          cache: 'no-store',
        }
      );
      if (!res.ok) throw new Error('Failed to fetch records');
      return res.json();
    },
    enabled: !!sourceId,
    // Disable frontend query caching for repository records.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
  });
}

/**
 * Hook for data source mutations (create, update, delete)
 */
export function useDataSourceMutations() {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  const create = useMutation({
    mutationFn: async (input: Omit<CreateDataSourceInput, 'orgId' | 'authorizedByUserId'>) => {
      const res = await authenticatedFetch('/api/data-sources', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create data source');
      }
      return res.json() as Promise<DataSourceResponse>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dataSourceKeys.all });
    },
  });

  const update = useMutation({
    mutationFn: async ({
      id,
      ...data
    }: {
      id: string;
      name?: string;
      description?: string | null;
      config?: Record<string, unknown>;
    }) => {
      const res = await authenticatedFetch(`/api/data-sources/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update data source');
      }
      return res.json() as Promise<DataSourceResponse>;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: dataSourceKeys.all });
      queryClient.invalidateQueries({
        queryKey: dataSourceKeys.detail(variables.id),
      });
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await authenticatedFetch(`/api/data-sources/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to delete data source');
      }
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: dataSourceKeys.all });
      queryClient.removeQueries({ queryKey: dataSourceKeys.detail(id) });
    },
  });

  const testConnection = useMutation({
    mutationFn: async (id: string) => {
      const res = await authenticatedFetch(`/api/data-sources/${id}/test`, {
        method: 'POST',
      });
      if (!res.ok) {
        const errorPayload = await res
          .json()
          .catch(() => null) as { error?: string; message?: string } | null;
        const detail = errorPayload?.error ?? errorPayload?.message ?? 'Connection test failed';
        throw new Error(`${detail} [HTTP ${res.status}]`);
      }
      return res.json() as Promise<{ success: boolean; error?: string; schema?: unknown }>;
    },
    onSuccess: (_, id) => {
      // Refetch schema after successful test
      queryClient.invalidateQueries({ queryKey: dataSourceKeys.schema(id) });
      queryClient.invalidateQueries({ queryKey: dataSourceKeys.detail(id) });
    },
  });

  return { create, update, remove, testConnection };
}
